import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTenant } from '../contexts/TenantContext';

const Collaterals = ({ apiBase }) => {
  const { tenant } = useTenant();

  const [collaterals, setCollaterals] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [channelPartners, setChannelPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedCollateral, setSelectedCollateral] = useState(null);
  const [sendRecipients, setSendRecipients] = useState([]);
  const [newRecipient, setNewRecipient] = useState({
    type: 'existing_client',
    id: '',
    name: '',
    email: '',
    phone: ''
  });
  const [filters, setFilters] = useState({
    project_id: '',
    category: '',
    status: '' // Show all by default, not just 'active'
  });

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    try {
      const [collateralsRes, projectsRes, clientsRes, partnersRes] = await Promise.all([
        axios.get(`${apiBase}/collaterals`, { params: filters }),
        axios.get(`${apiBase}/projects`),
        axios.get(`${apiBase}/clients/list`).catch(() => ({ data: [] })),
        axios.get(`${apiBase}/channel-partners`).catch(() => ({ data: [] }))
      ]);

      setCollaterals(Array.isArray(collateralsRes.data) ? collateralsRes.data : []);
      setProjects(Array.isArray(projectsRes.data) ? projectsRes.data : []);
      setClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);
      setChannelPartners(Array.isArray(partnersRes.data) ? partnersRes.data : []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    // Validate file is selected
    const fileInput = e.target.querySelector('input[type="file"]');
    if (!fileInput || !fileInput.files || fileInput.files.length === 0) {
      alert('Please select a file to upload');
      return;
    }
    
    try {
      const response = await axios.post(`${apiBase}/collaterals`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          // Silent progress tracking - no console log
        }
      });
      
      // Success - close modal and refresh
      setShowUploadModal(false);
      e.target.reset();
      // Refresh data immediately
      await fetchData();
      // Show brief success notification
      alert('✓ Collateral uploaded successfully!');
    } catch (error) {
      console.error('Error uploading collateral:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error occurred';
      alert('Error: ' + errorMessage);
    }
  };

  const handleSendCollateral = async () => {
    if (sendRecipients.length === 0) {
      alert('Please add at least one recipient');
      return;
    }

    try {
      await axios.post(`${apiBase}/collaterals/${selectedCollateral.id}/send`, {
        recipients: sendRecipients,
        notes: '',
        sent_by: 1
      });
      
      alert(`Collateral sent to ${sendRecipients.length} recipient(s) successfully!`);
      setShowSendModal(false);
      setSendRecipients([]);
      setSelectedCollateral(null);
      fetchData();
    } catch (error) {
      console.error('Error sending collateral:', error);
      alert('Error sending collateral: ' + (error.response?.data?.error || error.message));
    }
  };

  const addRecipient = () => {
    if (newRecipient.type === 'existing_client' || newRecipient.type === 'existing_partner') {
      if (!newRecipient.id) {
        alert('Please select a recipient');
        return;
      }
      
      const source = newRecipient.type === 'existing_client' ? clients : channelPartners;
      const recipient = source.find(r => r.id === parseInt(newRecipient.id));
      
      if (recipient) {
        setSendRecipients([...sendRecipients, {
          type: newRecipient.type,
          id: recipient.id,
          name: recipient.name,
          email: recipient.email || '',
          phone: recipient.phone || ''
        }]);
      }
    } else {
      if (!newRecipient.name || (!newRecipient.email && !newRecipient.phone)) {
        alert('Please fill in name and at least email or phone');
        return;
      }
      
      setSendRecipients([...sendRecipients, {
        type: newRecipient.type,
        id: null,
        name: newRecipient.name,
        email: newRecipient.email,
        phone: newRecipient.phone
      }]);
    }
    
    setNewRecipient({
      type: newRecipient.type,
      id: '',
      name: '',
      email: '',
      phone: ''
    });
  };

  const removeRecipient = (index) => {
    setSendRecipients(sendRecipients.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('image')) return '🖼️';
    if (fileType?.includes('pdf')) return '📄';
    if (fileType?.includes('word') || fileType?.includes('document')) return '📝';
    if (fileType?.includes('excel') || fileType?.includes('spreadsheet')) return '📊';
    if (fileType?.includes('powerpoint') || fileType?.includes('presentation')) return '📽️';
    if (fileType?.includes('video')) return '🎥';
    return '📎';
  };

  if (loading) {
    return <div className="loading">Loading collaterals...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1>Marketing Collaterals</h1>
        <button className="btn btn-primary" onClick={() => setShowUploadModal(true)}>
          + Upload Collateral
        </button>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <div>
            <label>Project</label>
            <select
              className="form-control"
              value={filters.project_id}
              onChange={(e) => setFilters({ ...filters, project_id: e.target.value })}
            >
              <option value="">All Projects</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label>Category</label>
            <select
              className="form-control"
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            >
              <option value="">All Categories</option>
              <option value="render">Render</option>
              <option value="brochure">Brochure</option>
              <option value="floor_plan">Floor Plan</option>
              <option value="price_list">Price List</option>
              <option value="video">Video</option>
              <option value="presentation">Presentation</option>
              <option value="cost_sheet">Cost Sheet</option>
              <option value="general">General</option>
            </select>
          </div>
          <div>
            <label>Status</label>
            <select
              className="form-control"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          <div>
            <label>&nbsp;</label>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setFilters({ project_id: '', category: '', status: '' });
                fetchData();
              }}
              style={{ width: '100%' }}
            >
              🔄 Refresh List
            </button>
          </div>
        </div>
      </div>

      {/* Collaterals List */}
      <div className="card">
        <h2 className="card-title">Collaterals ({collaterals.length})</h2>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Project</th>
                <th>File</th>
                <th>Size</th>
                <th>Uploaded</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {collaterals.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>
                    No collaterals found. Upload your first collateral!
                  </td>
                </tr>
              ) : (
                collaterals.map((collateral) => (
                  <tr key={collateral.id}>
                    <td>
                      <strong>{collateral.name}</strong>
                      {collateral.description && (
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                          {collateral.description}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="badge" style={{ 
                        backgroundColor: '#2563eb20',
                        color: '#2563eb'
                      }}>
                        {collateral.category || 'general'}
                      </span>
                    </td>
                    <td>{collateral.project_name || '-'}</td>
                    <td>
                      <span style={{ marginRight: '8px' }}>
                        {getFileIcon(collateral.file_type)}
                      </span>
                      {collateral.file_name}
                    </td>
                    <td>{formatFileSize(collateral.file_size || 0)}</td>
                    <td>{new Date(collateral.created_at).toLocaleDateString()}</td>
                    <td>
                      <span className="badge" style={{ 
                        backgroundColor: collateral.is_active ? '#10b98120' : '#6b728020',
                        color: collateral.is_active ? '#10b981' : '#6b7280'
                      }}>
                        {collateral.is_active ? 'Active' : 'Archived'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {(collateral.file_type?.includes('image') || collateral.file_type?.includes('video')) && (
                          <button
                            className="btn btn-sm"
                            onClick={() => {
                              setSelectedCollateral(collateral);
                              setShowPreviewModal(true);
                            }}
                            style={{ 
                              background: '#10b981',
                              color: 'white',
                              fontSize: '11px',
                              padding: '4px 8px',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            👁️ View
                          </button>
                        )}
                        <a
                          href={`${apiBase}/collaterals/${collateral.id}/download`}
                          className="btn btn-sm btn-secondary"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: '11px', padding: '4px 8px', textDecoration: 'none' }}
                        >
                          ⬇️ Download
                        </a>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => {
                            setSelectedCollateral(collateral);
                            setShowSendModal(true);
                            setSendRecipients([]);
                          }}
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                        >
                          📤 Send
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)} style={{ zIndex: 10000 }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ 
            zIndex: 10001, 
            background: 'white',
            maxWidth: '600px',
            width: '90%',
            borderRadius: '12px',
            padding: '0'
          }}>
            {/* Modal Header */}
            <div style={{ 
              padding: '24px 24px 20px 24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)'
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#1f2937' }}>
                  📎 Upload Marketing Collateral
                </h2>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                  Add a new marketing material to your library
                </p>
              </div>
              <button 
                className="modal-close" 
                onClick={() => setShowUploadModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '28px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '6px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => { e.target.style.background = '#f3f4f6'; e.target.style.color = '#1f2937'; }}
                onMouseLeave={(e) => { e.target.style.background = 'none'; e.target.style.color = '#6b7280'; }}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleFileUpload} style={{ padding: '24px' }}>
              {/* File Upload Section - Prominent */}
              <div className="form-group" style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '12px', 
                  fontWeight: '600', 
                  color: '#374151',
                  fontSize: '15px'
                }}>
                  Select File <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <div style={{
                  border: '2px dashed #d1d5db',
                  borderRadius: '8px',
                  padding: '32px',
                  textAlign: 'center',
                  background: '#f9fafb',
                  transition: 'all 0.3s',
                  cursor: 'pointer',
                  position: 'relative'
                }}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.background = '#eff6ff'; }}
                onDragLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.background = '#f9fafb'; }}
                onDrop={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.background = '#f9fafb'; }}
                >
                  <input
                    type="file"
                    name="file"
                    required
                    accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,video/*"
                    onChange={(e) => {
                      const fileName = e.target.files[0]?.name || 'No file chosen';
                      const label = e.currentTarget.parentElement.querySelector('.file-label');
                      if (label) label.textContent = fileName;
                    }}
                    style={{
                      position: 'absolute',
                      opacity: 0,
                      width: '100%',
                      height: '100%',
                      cursor: 'pointer',
                      top: 0,
                      left: 0
                    }}
                  />
                  <div style={{ pointerEvents: 'none' }}>
                    <div style={{ fontSize: '48px', marginBottom: '12px' }}>📤</div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                      Click to upload or drag and drop
                    </div>
                    <div className="file-label" style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                      No file chosen
                    </div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '12px' }}>
                      Supported: Images, PDF, Documents, Videos (Max 50MB)
                    </div>
                  </div>
                </div>
              </div>

              {/* Name Field */}
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600', 
                  color: '#374151',
                  fontSize: '15px'
                }}>
                  Collateral Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="name"
                  required
                  placeholder="e.g., Project Brochure"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '15px',
                    transition: 'all 0.2s'
                  }}
                  onFocus={(e) => { e.target.style.borderColor = '#2563eb'; e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                />
              </div>

              {/* Description Field */}
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600', 
                  color: '#374151',
                  fontSize: '15px'
                }}>
                  Description
                </label>
                <textarea
                  className="form-control"
                  name="description"
                  rows="3"
                  placeholder="Brief description of the collateral (optional)"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '15px',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    transition: 'all 0.2s'
                  }}
                  onFocus={(e) => { e.target.style.borderColor = '#2563eb'; e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                />
              </div>

              {/* Category and Project - Side by Side */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div className="form-group">
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    fontWeight: '600', 
                    color: '#374151',
                    fontSize: '15px'
                  }}>
                    Category
                  </label>
                  <select 
                    className="form-control" 
                    name="category"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '15px',
                      background: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => { e.target.style.borderColor = '#2563eb'; e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                  >
                    <option value="general">General</option>
                    <option value="render">Render</option>
                    <option value="brochure">Brochure</option>
                    <option value="floor_plan">Floor Plan</option>
                    <option value="price_list">Price List</option>
                    <option value="video">Video</option>
                    <option value="presentation">Presentation</option>
                    <option value="cost_sheet">Cost Sheet</option>
                  </select>
                </div>
                <div className="form-group">
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    fontWeight: '600', 
                    color: '#374151',
                    fontSize: '15px'
                  }}>
                    Project <span style={{ fontSize: '12px', fontWeight: '400', color: '#6b7280' }}>(Optional)</span>
                  </label>
                  <select 
                    className="form-control" 
                    name="project_id"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '15px',
                      background: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => { e.target.style.borderColor = '#2563eb'; e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                  >
                    <option value="">Select Project</option>
                    {projects.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <input type="hidden" name="uploaded_by" value="1" />

              {/* Action Buttons */}
              <div style={{ 
                display: 'flex', 
                gap: '12px', 
                justifyContent: 'flex-end', 
                marginTop: '32px',
                paddingTop: '24px',
                borderTop: '1px solid #e5e7eb'
              }}>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowUploadModal(false)}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '15px'
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  style={{
                    padding: '12px 32px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '15px',
                    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                    boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3)'
                  }}
                >
                  📤 Upload Collateral
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Send Modal */}
      {showSendModal && selectedCollateral && (
        <div className="modal-overlay" onClick={() => setShowSendModal(false)}>
          <div className="modal-content" style={{ maxWidth: '700px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Send Collateral: {selectedCollateral.name}</h2>
              <button className="modal-close" onClick={() => setShowSendModal(false)}>×</button>
            </div>
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ marginBottom: '16px' }}>Add Recipients</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label>Recipient Type</label>
                  <select
                    className="form-control"
                    value={newRecipient.type}
                    onChange={(e) => setNewRecipient({ ...newRecipient, type: e.target.value, id: '' })}
                  >
                    <option value="existing_client">Existing Client</option>
                    <option value="new_client">New Client</option>
                    <option value="existing_partner">Existing Channel Partner</option>
                    <option value="new_partner">New Channel Partner</option>
                  </select>
                </div>
                {(newRecipient.type === 'existing_client' || newRecipient.type === 'existing_partner') ? (
                  <div>
                    <label>Select {newRecipient.type === 'existing_client' ? 'Client' : 'Partner'}</label>
                    <select
                      className="form-control"
                      value={newRecipient.id}
                      onChange={(e) => setNewRecipient({ ...newRecipient, id: e.target.value })}
                    >
                      <option value="">Select...</option>
                      {(newRecipient.type === 'existing_client' ? clients : channelPartners).map(item => (
                        <option key={item.id} value={item.id}>
                          {item.name} {item.phone ? `(${item.phone})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <>
                    <div>
                      <label>Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newRecipient.name}
                        onChange={(e) => setNewRecipient({ ...newRecipient, name: e.target.value })}
                        placeholder="Name"
                      />
                    </div>
                    <div>
                      <label>Email</label>
                      <input
                        type="email"
                        className="form-control"
                        value={newRecipient.email}
                        onChange={(e) => setNewRecipient({ ...newRecipient, email: e.target.value })}
                        placeholder="Email"
                      />
                    </div>
                    <div>
                      <label>Phone *</label>
                      <input
                        type="tel"
                        className="form-control"
                        value={newRecipient.phone}
                        onChange={(e) => setNewRecipient({ ...newRecipient, phone: e.target.value })}
                        placeholder="Phone"
                        required
                      />
                    </div>
                  </>
                )}
              </div>
              <button type="button" className="btn btn-secondary" onClick={addRecipient}>
                + Add Recipient
              </button>
            </div>

            {sendRecipients.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ marginBottom: '12px' }}>Recipients ({sendRecipients.length})</h3>
                <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px' }}>
                  {sendRecipients.map((recipient, idx) => (
                    <div key={idx} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '8px',
                      background: '#f8fafc',
                      borderRadius: '4px',
                      marginBottom: '8px'
                    }}>
                      <div>
                        <strong>{recipient.name}</strong>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          {recipient.email && `${recipient.email} • `}
                          {recipient.phone}
                          <span style={{ marginLeft: '8px', fontSize: '11px' }}>
                            ({recipient.type.replace('_', ' ')})
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => removeRecipient(idx)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowSendModal(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSendCollateral}
                disabled={sendRecipients.length === 0}
              >
                Send to {sendRecipients.length} Recipient(s)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedCollateral && (
        <div className="modal-overlay" onClick={() => setShowPreviewModal(false)} style={{ zIndex: 10000 }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ 
            zIndex: 10001, 
            background: 'white',
            maxWidth: '90%',
            maxHeight: '90vh',
            padding: '0'
          }}>
            <div style={{ 
              padding: '20px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                  {selectedCollateral.name}
                </h2>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                  {selectedCollateral.file_name} • {formatFileSize(selectedCollateral.file_size || 0)}
                </p>
              </div>
              <button 
                className="modal-close" 
                onClick={() => setShowPreviewModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '28px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '0',
                  width: '32px',
                  height: '32px'
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '20px', textAlign: 'center', maxHeight: '70vh', overflow: 'auto' }}>
              {selectedCollateral.file_type?.includes('image') ? (
                <img 
                  src={`${apiBase}/collaterals/${selectedCollateral.id}/download`}
                  alt={selectedCollateral.name}
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '60vh',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}
                />
              ) : selectedCollateral.file_type?.includes('video') ? (
                <video 
                  controls 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '60vh',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}
                >
                  <source src={`${apiBase}/collaterals/${selectedCollateral.id}/download`} type={selectedCollateral.file_type} />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <div style={{ padding: '40px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
                  <p style={{ fontSize: '16px', color: '#6b7280' }}>
                    Preview not available for this file type.
                  </p>
                  <a
                    href={`${apiBase}/collaterals/${selectedCollateral.id}/download`}
                    className="btn btn-primary"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ marginTop: '16px', textDecoration: 'none', display: 'inline-block' }}
                  >
                    Download to View
                  </a>
                </div>
              )}
              {selectedCollateral.description && (
                <div style={{ marginTop: '20px', padding: '16px', background: '#f9fafb', borderRadius: '8px', textAlign: 'left' }}>
                  <strong>Description:</strong>
                  <p style={{ margin: '8px 0 0 0', color: '#374151' }}>{selectedCollateral.description}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Collaterals;

